/**
 * The join links in Add machine (joinTickets.ts): the link made by the last download, this
 * person's open links (read when the dialog opens and after each change), and cancelling one.
 * Off (the flag) it does nothing and fetches nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JoinTicket } from '@orgus/protocol';
import { copyText, toUiError, useNow } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { demoJoinTickets, liveJoinTickets, makeCloudInit, makeJoinLink, openTickets } from './joinTickets.ts';
import type { CloudInitState, JoinLinkState, JoinTicketSource } from './joinTickets.ts';

export type JoinListState = { kind: 'loading' } | { kind: 'ready'; tickets: JoinTicket[] } | { kind: 'error'; error: UiError };

export interface JoinTicketsState {
  on: boolean;
  link: JoinLinkState;
  list: JoinListState;
  now: number;
  /** The id being cancelled, and the last cancel that failed. */
  cancelling: string | null;
  cancelError: UiError | null;
  /** Make a link and copy it (the download button). Resolves when done; never throws. */
  make(): Promise<void>;
  /** Copy the current link again (the "Copy link" button); false when the browser refused. */
  copy(): Promise<boolean>;
  cancel(id: string): Promise<void>;
  reload(): Promise<void>;
  /** Cloud VM (ADR 0105): the cloud-init document made last, and making or copying one. */
  cloud: CloudInitState;
  makeCloud(o: { name?: string; hubUrl?: string; repoUrl?: string }): Promise<void>;
  copyCloud(): Promise<boolean>;
}

const demo = demoJoinTickets();

export function useJoinTickets(open: boolean, on: boolean): JoinTicketsState {
  const mock = useOrgus((s) => s.mode === 'mock');
  const source: JoinTicketSource = mock ? demo : liveJoinTickets;
  const [link, setLink] = useState<JoinLinkState>({ kind: 'idle' });
  const [list, setList] = useState<JoinListState>({ kind: 'loading' });
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<UiError | null>(null);
  const [cloud, setCloud] = useState<CloudInitState>({ kind: 'idle' });
  const now = useNow(15_000);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    try {
      const tickets = await source.list();
      if (alive.current) setList({ kind: 'ready', tickets });
    } catch (e) {
      if (alive.current) setList({ kind: 'error', error: toUiError(e) });
    }
  }, [source]);

  useEffect(() => {
    if (!open || !on) return;
    setLink({ kind: 'idle' });
    setCloud({ kind: 'idle' });
    setCancelError(null);
    setList({ kind: 'loading' });
    void reload();
  }, [open, on, reload]);

  const make = useCallback(async () => {
    if (!on) return;
    setLink({ kind: 'making' });
    const next = await makeJoinLink({ mint: () => source.mint(), copy: copyText });
    if (!alive.current) return;
    setLink(next);
    if (next.kind !== 'error') void reload();
  }, [on, source, reload]);

  const copy = useCallback(async () => {
    const l = link.kind === 'copied' || link.kind === 'not-copied' ? link.link : null;
    if (!l) return false;
    const ok = await copyText(l.url);
    if (alive.current) setLink({ kind: ok ? 'copied' : 'not-copied', link: l });
    return ok;
  }, [link]);

  const makeCloud = useCallback(
    async (o: { name?: string; hubUrl?: string; repoUrl?: string }) => {
      if (!on) return;
      setCloud({ kind: 'making' });
      const next = await makeCloudInit({ mint: () => source.mint(), copy: copyText, ...o });
      if (!alive.current) return;
      setCloud(next);
      if (next.kind === 'ready') void reload();
    },
    [on, source, reload],
  );

  const copyCloud = useCallback(async () => {
    if (cloud.kind !== 'ready') return false;
    const ok = await copyText(cloud.userData);
    if (alive.current) setCloud({ ...cloud, copied: ok });
    return ok;
  }, [cloud]);

  const cancel = useCallback(
    async (id: string) => {
      setCancelling(id);
      setCancelError(null);
      try {
        await source.cancel(id);
        if (!alive.current) return;
        // The link just made was the one cancelled: it no longer joins anything.
        setLink((l) => ((l.kind === 'copied' || l.kind === 'not-copied') && l.link.id === id ? { kind: 'idle' } : l));
        setCloud((c) => (c.kind === 'ready' && c.link.id === id ? { kind: 'idle' } : c));
        await reload();
      } catch (e) {
        if (alive.current) setCancelError(toUiError(e));
      } finally {
        if (alive.current) setCancelling(null);
      }
    },
    [source, reload],
  );

  const shown = useMemo<JoinListState>(() => (list.kind === 'ready' ? { kind: 'ready', tickets: openTickets(list.tickets, now) } : list), [list, now]);
  return { on, link, list: shown, now, cancelling, cancelError, make, copy, cancel, reload, cloud, makeCloud, copyCloud };
}
